import type { AccessTokenProvider, IdentityScope, TokenRequestReason } from "@/auth/access-token-provider";
import type { TokenRequiredReason } from "./protocol";

/**
 * 嵌入端 Token Provider（设计 §9.2）。
 *
 * 与独立端最关键的差别是 `onAuthenticationFailed`：**任何失败路径都不做顶层导航**，
 * 更不会调用 `oidc.loginAsync()` 在 iframe 里跳 IAM 登录页（AR-27）。
 * Token 只存在于这个对象的内存里——不进 URL、不进任何浏览器存储、不进日志（§12.3）。
 */

/** Host Provider 挂上来的出口，把 Provider 的诉求变成父子协议消息。 */
export type EmbeddedTokenBridge = {
  requestToken: (reason: TokenRequiredReason) => void;
  endSession: (reason: TokenRequiredReason) => void;
};

/** 等待宿主交付新 Token 的上限。超时按"拿不到 Token"处理，不重复催。 */
const TOKEN_REQUEST_TIMEOUT_MS = 15_000;

/** 仅用于会话缓存作用域，绝不用于判权。签名校验是 Api.Admin 的事。 */
function readSubjectClaim(token: string): { subject: string; expiresAt: number } | null {
  const payloadSegment = token.split(".")[1];
  if (!payloadSegment) {
    return null;
  }

  try {
    const base64 = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const json = JSON.parse(
      decodeURIComponent(
        Array.from(atob(padded))
          .map((character) => `%${character.charCodeAt(0).toString(16).padStart(2, "0")}`)
          .join(""),
      ),
    ) as Record<string, unknown>;

    const subject = typeof json.sub === "string" && json.sub.length > 0 ? json.sub : null;
    const exp = typeof json.exp === "number" && Number.isFinite(json.exp) ? json.exp * 1000 : null;
    return subject && exp ? { subject, expiresAt: exp } : null;
  } catch {
    return null;
  }
}

class EmbeddedExchangeAccessTokenProvider implements AccessTokenProvider {
  private token: string | null = null;
  private expiresAt = 0;
  private lastSeq = 0;
  private bridge: EmbeddedTokenBridge | null = null;
  private listeners = new Set<() => void>();
  private pending: { promise: Promise<string | null>; resolve: (token: string | null) => void; timer: number } | null = null;
  private scopeSource: string | null = null;
  private scope: IdentityScope = null;

  attachBridge(bridge: EmbeddedTokenBridge): () => void {
    this.bridge = bridge;
    return () => {
      if (this.bridge === bridge) {
        this.bridge = null;
      }
    };
  }

  /**
   * 应用宿主推来的 Token。序号必须严格递增：异步响应乱序时旧 Token 不能覆盖新 Token（AR-06）。
   * 返回是否被接受，便于宿主侧问题在遥测里可见。
   */
  applyToken(seq: number, accessToken: string, expiresAt: string): boolean {
    if (seq <= this.lastSeq) {
      return false;
    }

    this.lastSeq = seq;
    this.token = accessToken;
    this.expiresAt = Date.parse(expiresAt);
    this.settlePending(accessToken);
    this.notify();
    return true;
  }

  clear(): void {
    this.token = null;
    this.expiresAt = 0;
    this.scopeSource = null;
    this.scope = null;
    this.settlePending(null);
    this.notify();
  }

  /** 宿主会话结束或 iframe 卸载：连序号一起丢，重新握手后从头开始。 */
  reset(): void {
    this.lastSeq = 0;
    this.clear();
  }

  getSnapshot(): string | null {
    return this.token;
  }

  getExpiresAt(): number {
    return this.expiresAt;
  }

  async getAccessToken(): Promise<string | null> {
    return this.token;
  }

  /**
   * 向宿主要新 Token。并发调用共享同一个 Promise，
   * 避免一批 401 同时触发多次换票形成刷新风暴（§6.5、AR-15）。
   */
  requestAccessToken(reason: TokenRequestReason): Promise<string | null> {
    if (this.pending) {
      return this.pending.promise;
    }

    let resolve: (token: string | null) => void = () => {};
    const promise = new Promise<string | null>((resolvePromise) => {
      resolve = resolvePromise;
    });
    const timer = window.setTimeout(() => {
      this.settlePending(null);
    }, TOKEN_REQUEST_TIMEOUT_MS);

    this.pending = { promise, resolve, timer };
    this.bridge?.requestToken(reason);
    return promise;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getIdentityScope(): IdentityScope {
    if (this.token === this.scopeSource) {
      return this.scope;
    }

    this.scopeSource = this.token;
    if (!this.token) {
      this.scope = null;
      return null;
    }

    const claims = readSubjectClaim(this.token);
    // 嵌入端没有 ID Token，作用域取交换 Token 的 sub + exp（设计 §9.2）。
    this.scope = claims ? { key: JSON.stringify(["embedded", claims.subject, claims.expiresAt]), expiresAt: claims.expiresAt } : null;
    return this.scope;
  }

  async onAuthenticationFailed(reason: TokenRequestReason): Promise<void> {
    // 换票后仍然 401：结束嵌入会话并告知宿主。不跳转、不加载 IAM 任何页面。
    this.clear();
    this.bridge?.endSession(reason);
  }

  private settlePending(token: string | null) {
    if (!this.pending) {
      return;
    }
    const pending = this.pending;
    this.pending = null;
    window.clearTimeout(pending.timer);
    pending.resolve(token);
  }

  private notify() {
    this.listeners.forEach((listener) => {
      listener();
    });
  }
}

export const embeddedAccessTokenProvider = new EmbeddedExchangeAccessTokenProvider();
